/**
 * Customer auth endpoint modules — thin, hand-written, typed by the generated
 * OpenAPI schemas (@dp/api). These are the ONLY place customer auth wire
 * shapes are referenced by name.
 */
import { uuid, type CustomerComponents } from "@dp/api";
import { getClient } from "./client.js";

type Schemas = CustomerComponents["schemas"];
export type ContactType = Schemas["ContactType"];
export type OtpChallenge = Schemas["OtpChallenge"];
export type AuthCompletionGrant = Schemas["AuthCompletionGrant"];
export type BrowserSession = Schemas["BrowserSession"];
export type CustomerProfile = Schemas["CustomerProfile"];

export interface Contact {
  contact_type: ContactType;
  contact_value: string;
}

// ---- registration ----

export function requestRegistrationOtp(contact: Contact): Promise<OtpChallenge> {
  return getClient().request<OtpChallenge>("/auth/register/otp/request", {
    method: "POST",
    body: contact,
    skipAuth: true,
  });
}

export function verifyRegistrationOtp(
  contact: Contact,
  code: string,
): Promise<AuthCompletionGrant> {
  return getClient().request<AuthCompletionGrant>("/auth/register/otp/verify", {
    method: "POST",
    body: { ...contact, code },
    skipAuth: true,
  });
}

export function completeRegistration(
  completionToken: string,
  password: string,
): Promise<BrowserSession> {
  return getClient().request<BrowserSession>("/auth/register/complete", {
    method: "POST",
    body: { completion_token: completionToken, password },
    skipAuth: true,
  });
}

// ---- login / logout / refresh ----

export function passwordLogin(contact: Contact, password: string): Promise<BrowserSession> {
  return getClient().request<BrowserSession>("/auth/login", {
    method: "POST",
    body: { ...contact, password },
    skipAuth: true,
  });
}

export function logout(): Promise<unknown> {
  return getClient().request("/auth/logout", { method: "POST", body: {}, skipAuth: true });
}

// ---- password reset (forgot) ----

export function requestPasswordResetOtp(contact: Contact): Promise<OtpChallenge> {
  return getClient().request<OtpChallenge>("/auth/password/forgot/request", {
    method: "POST",
    body: contact,
    skipAuth: true,
  });
}

export function verifyPasswordResetOtp(
  contact: Contact,
  code: string,
): Promise<AuthCompletionGrant> {
  return getClient().request<AuthCompletionGrant>("/auth/password/forgot/verify", {
    method: "POST",
    body: { ...contact, code },
    skipAuth: true,
  });
}

export function resetPassword(
  completionToken: string,
  password: string,
): Promise<BrowserSession> {
  return getClient().request<BrowserSession>("/auth/password/reset", {
    method: "POST",
    body: { completion_token: completionToken, password },
    skipAuth: true,
  });
}

// ---- profile ----

export function getMe(): Promise<CustomerProfile> {
  return getClient().request<CustomerProfile>("/me");
}

/** Idempotency-Key for a logical action; generate at form mount, reuse on retry. */
export { uuid as newIdempotencyKey };
